import React, { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { Duration } from 'luxon';
import { useTranslation } from '../../localization/useTranslation';
import { Button, Flex, FlexItem, FormGroup } from '@patternfly/react-core';
import { DateTime } from 'luxon';

import './Chart.css';
import { Aggregation, AggregationTranslation, TimeBucket, timeBucketData, TimeBucketTranslation, TimeOffset } from '../../services/constants';
import SelectV2 from 'components/Select/SelectV2';
import { TimeSelector } from '../TimeRangeSelector/NewTimeRangeSelector';
import { TimeOffsetToMinutes } from '../../services/constants';
import ChartViewData from './ChartViewData';
import PropTypes from 'prop-types';

const ViewOptions = _.values(ChartViewData);

const ChartToolbar = ({
  dataOptions: dataOptionsProp,
  onDataOptionsChange,
  onLoading,
  setResetZoom,
  toggleDrawer,
  chosenSignals: selectedAssets,
  setCurrentViewChooserOption,
  currentViewChooserOption,
  showSelectSignals = true,
  className
}) => {
  const T = useTranslation();
  const [aggregationItems, defaultDataOptions, bucketItems] = useMemo(() => {
    // for the Select component options value property is required
    const _aggItems = _.map(AggregationTranslation, (text, id) => ({ id, value: id, text: T.localize(text) }));
    const _bucketItems = _.map(TimeBucketTranslation, (text, id) => ({ id, value: id, text: T.localize(text) }));
    const defaultAgg = _.find(_aggItems, { id: Aggregation.MEAN });
    const defaultBucket = _.find(_bucketItems, { id: TimeBucket.PT5M });
    defaultBucket.selected = true;
    defaultAgg.selected = true;
    const _dataOptions = {
      signalSets: [],
      aggregate: Duration.fromISO(defaultBucket.id),
      timespan: {
        from: DateTime.now().minus({ Minutes: TimeOffsetToMinutes[TimeOffset.T6HOURS] }),
        to: DateTime.now() },
      aggregationFunction: defaultAgg.id
    };
    return [_aggItems, _dataOptions, _bucketItems];
  }, [T]);

  const [aggregationFunctionState, setAggregationFunctionState] = useState(_.find(aggregationItems, 'selected').id);
  const [timeBucketState, setTimeBucketState] = useState(_.find(bucketItems, 'selected').id);

  const [aggregate, setAggregate] = useState(defaultDataOptions.aggregate);

  const [dataOptions, setDataOptions] = useState(dataOptionsProp ?? defaultDataOptions);

  const [aggregationItems_, setAggregationItems_] = useState(aggregationItems);

  useEffect(() => {
    const availableAggregationItems = _.intersection(
      ChartViewData[currentViewChooserOption].aggregation,
      timeBucketData[timeBucketState].aggregation
    );
    const filteredAggregationItems = _.filter(aggregationItems, item => availableAggregationItems.includes(item.id));

    setAggregationItems_(filteredAggregationItems);
  }, [aggregationItems, timeBucketState, currentViewChooserOption]);

  const onAggregationFunctionStateChange = useCallback((aggFuncState) => {
    setAggregationFunctionState(aggFuncState);

    if (timeBucketState === TimeBucket.RAW) {
      setTimeBucketState(TimeBucket.PT5M);
    }
  }, [timeBucketState]);

  const onTimeBucketStateChange = useCallback((time) => {
    setTimeBucketState(time);
    if (time !== TimeBucket.RAW) {
      setAggregate(Duration.fromISO(time));
    } else {
      setAggregate(time);
    }
  }, []);

  const onApplyTimeSpan = useCallback((timeSpan) => {
    setDataOptions(oldDataOptions => ({
      ...oldDataOptions,
      timespan: timeSpan
    }));
    setResetZoom(true);
  }, [setResetZoom]);

  useEffect(() => {
    const aggState = (timeBucketState !== TimeBucket.RAW) ? aggregationFunctionState : null;
    const signalSets = _.map(selectedAssets, asset => {
      return {
        ...asset,
        signals: _.map(asset.signals, signal => ({
          ...signal,
          aggregate: aggState
        }))
      };
    });

    setDataOptions(oldDataOptions => ({
      ...oldDataOptions,
      signalSets,
      aggregate: timeBucketState,
      aggregationFunction: aggState
    }));
  }, [aggregate, timeBucketState, selectedAssets, aggregationFunctionState]);

  useEffect(() => {
    if (onDataOptionsChange) {
      onDataOptionsChange(dataOptions);
    }
  }, [dataOptions, onDataOptionsChange]);

  return (
    <Flex className={className}>
      { showSelectSignals && (<FlexItem>
        <Button
          isLoading={onLoading}
          iconPosition="right"
          onClick={toggleDrawer}>{ T.graph.toolbar.selectSignals }</Button>
      </FlexItem>)
      }
      <FlexItem align={{ default: 'alignRight' }}>
        <FormGroup fieldId="viewChooser"
          label="View"
        >
          <SelectV2 value={currentViewChooserOption} onChange={setCurrentViewChooserOption} options={ViewOptions} />
          { /* <SelectV2 onChange={setCurrentViewChooserOption} options={ViewOptions} /> */ }
        </FormGroup>
      </FlexItem>
      <FlexItem>
        <FormGroup fieldId="aggregationChooser"
          label="Aggregation"
        >
          <SelectV2 value={aggregationFunctionState} onChange={onAggregationFunctionStateChange} options={aggregationItems_} />
        </FormGroup>
      </FlexItem>
      <FlexItem >
        <FormGroup fieldId="timeBucketChooser"
          label="Time bucket"
        >
          <SelectV2 value={timeBucketState} onChange={onTimeBucketStateChange} options={bucketItems} />
        </FormGroup>
      </FlexItem>
      <FlexItem >
        <TimeSelector initialTimeSpan={dataOptions.timespan} onApply={onApplyTimeSpan} />
      </FlexItem>
    </Flex>
  );
};

ChartToolbar.propTypes = {
  dataOptions: PropTypes.object,
  onDataOptionsChange: PropTypes.func,
  onLoading: PropTypes.bool,
  setResetZoom: PropTypes.func,
  toggleDrawer: PropTypes.func,
  chosenSignals: PropTypes.array,
  setCurrentViewChooserOption: PropTypes.func,
  currentViewChooserOption: PropTypes.string,
  className: PropTypes.string
};

export default ChartToolbar;
